import type { ReactElement } from 'react'
import type {
  HabitDashboardMetric,
  HabitDashboardStats,
} from '../utils/habitStats'

interface HabitDashboardProps {
  stats: HabitDashboardStats
}

interface StatRowProps {
  label: string
  metric: HabitDashboardMetric
}

function StatRow({ label, metric }: StatRowProps): ReactElement {
  return (
    <div className="flex flex-col gap-1.5">
      <div className="flex items-center justify-between gap-3">
        <span className="font-medium text-gray-700 text-sm">{label}</span>
        <span className="text-gray-500 text-xs">
          {metric.completed}/{metric.expected} · {metric.percent}%
        </span>
      </div>
      <div className="h-2 overflow-hidden rounded-full bg-gray-100">
        <div
          className="h-full rounded-full bg-indigo-600 transition-all"
          style={{ width: `${Math.min(100, metric.percent)}%` }}
        />
      </div>
    </div>
  )
}

export function HabitDashboard({
  stats,
}: HabitDashboardProps): ReactElement | null {
  if (stats.week.expected === 0 && stats.month.expected === 0) return null

  return (
    <section
      aria-label="Habit completion dashboard"
      className="mb-4 rounded-xl border border-gray-200 bg-white p-4 shadow-sm"
    >
      <div className="mb-3 flex items-center justify-between">
        <div>
          <h2 className="font-semibold text-base text-gray-900">Progress</h2>
          <p className="text-gray-500 text-xs">Completion stats</p>
        </div>
        <span className="text-2xl" aria-hidden="true">
          📊
        </span>
      </div>
      <div className="flex flex-col gap-3">
        <StatRow label="This week" metric={stats.week} />
        <StatRow label="This month" metric={stats.month} />
      </div>
    </section>
  )
}
